import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SummaryCard } from './SummaryCard';
import { MarginHistory } from '@/types/primaryLedger';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ColumnConfig {
  id: string;
  label: string;
  accessor: (item: MarginHistory) => string;
  align?: 'left' | 'right';
}

interface MarginSectionProps {
  data: MarginHistory[];
  loading: boolean;
}

export const MarginSection: React.FC<MarginSectionProps> = ({ data, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [visibleSeries, setVisibleSeries] = useState({
    price: true,
    finalGrossProfit: true
  });

  const defaultColumns: ColumnConfig[] = [
    { id: 'date', label: 'Date', accessor: (item) => item.date?.toDate?.().toLocaleDateString() ?? 'N/A', align: 'left' },
    { id: 'price', label: 'Price', accessor: (item) => `$${item.price.toFixed(2)}`, align: 'right' },
    { id: 'cogs', label: 'COGS', accessor: (item) => `$${item.cogs.toFixed(2)}`, align: 'right' },
    { id: 'fbaFee', label: 'FBA Fee', accessor: (item) => `$${item.fbaFee.toFixed(2)}`, align: 'right' },
    { id: 'referralFeePercent', label: 'Referral Fee %', accessor: (item) => `${item.referralFeePercent.toFixed(2)}%`, align: 'right' },
    { id: 'grossProfitBufferPercent', label: 'GP Buffer %', accessor: (item) => `${item.grossProfitBufferPercent.toFixed(2)}%`, align: 'right' },
    { id: 'actualGrossProfit', label: 'Actual GP', accessor: (item) => `$${item.actualGrossProfit.toFixed(2)}`, align: 'right' },
    { id: 'finalGrossProfitDollar', label: 'Final GP $', accessor: (item) => `$${item.finalGrossProfitDollar.toFixed(2)}`, align: 'right' },
    { id: 'finalTargetAcos', label: 'Target ACoS', accessor: (item) => `${item.finalTargetAcos.toFixed(2)}%`, align: 'right' },
  ];

  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);

  useEffect(() => {
    const saved = localStorage.getItem('margin-columns');
    if (saved) {
      try {
        const savedColumnOrder: Array<{id: string}> = JSON.parse(saved);
        const restoredColumns = savedColumnOrder
          .map(savedCol => defaultColumns.find(dc => dc.id === savedCol.id))
          .filter((col): col is ColumnConfig => col !== undefined);
        
        if (restoredColumns.length > 0) {
          setColumns(restoredColumns);
        }
      } catch (e) {
        console.error('Failed to parse saved columns:', e);
      }
    }
  }, []);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, removed);
    
    setColumns(newColumns);
    localStorage.setItem('margin-columns', JSON.stringify(newColumns));
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleToggleSeries = (dataKey: 'price' | 'finalGrossProfit') => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const CustomLegend = () => {
    const seriesConfig = [
      { key: 'price' as const, label: 'Price', color: '#8884d8' },
      { key: 'finalGrossProfit' as const, label: 'Final Gross Profit $', color: '#82ca9d' }
    ];

    return (
      <div className="flex justify-center gap-4 mt-4">
        {seriesConfig.map((series) => {
          const isVisible = visibleSeries[series.key];
          return (
            <button
              key={series.key}
              onClick={() => handleToggleSeries(series.key)}
              className={`flex items-center gap-2 px-3 py-1 rounded transition-all hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isVisible ? 'opacity-100' : 'opacity-50'
              }`}
              title={isVisible ? 'Click to hide' : 'Click to show'}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: series.color }}
              />
              <span className={`text-sm text-gray-700 dark:text-gray-300 ${!isVisible ? 'line-through' : ''}`}>
                {series.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm text-gray-600 dark:text-gray-400">
              <span style={{ color: entry.color }}>{entry.name}: </span>
              ${entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const calculateSummaryStats = () => {
    if (data.length === 0) {
      return {
        avgPrice: 0,
        avgCogs: 0,
        avgGrossProfitPercent: 0,
        avgFinalGrossProfitDollar: 0,
        avgTargetAcos: 0,
      };
    }

    const totalPrice = data.reduce((sum, item) => sum + item.price, 0);
    const totalCogs = data.reduce((sum, item) => sum + item.cogs, 0);
    const totalGrossProfitPercent = data.reduce((sum, item) => sum + item.grossProfitBufferPercent, 0);
    const totalFinalGrossProfitDollar = data.reduce((sum, item) => sum + item.finalGrossProfitDollar, 0);
    const totalTargetAcos = data.reduce((sum, item) => sum + item.finalTargetAcos, 0);

    return {
      avgPrice: totalPrice / data.length,
      avgCogs: totalCogs / data.length,
      avgGrossProfitPercent: totalGrossProfitPercent / data.length,
      avgFinalGrossProfitDollar: totalFinalGrossProfitDollar / data.length,
      avgTargetAcos: totalTargetAcos / data.length,
    };
  };

  const formatChartData = () => {
    return [...data]
      .sort((a, b) => {
        const dateA = a.date?.toDate?.() ?? new Date(0);
        const dateB = b.date?.toDate?.() ?? new Date(0);
        return dateA.getTime() - dateB.getTime();
      })
      .map(item => ({
        date: item.date?.toDate?.().toLocaleDateString() ?? 'N/A',
        price: item.price,
        finalGrossProfit: item.finalGrossProfitDollar,
      }));
  };

  const stats = calculateSummaryStats();
  const chartData = formatChartData();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          Loading margin data...
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          No margin data available for this date range
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Avg Price"
          value={`$${stats.avgPrice.toFixed(2)}`}
        />
        <SummaryCard
          title="Avg COGS"
          value={`$${stats.avgCogs.toFixed(2)}`}
        />
        <SummaryCard
          title="Avg Gross Profit %"
          value={`${stats.avgGrossProfitPercent.toFixed(2)}%`}
          subtitle={`$${stats.avgFinalGrossProfitDollar.toFixed(2)} Final`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Price & Gross Profit Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {visibleSeries.price && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="price"
                  stroke="#8884d8"
                  name="Price"
                />
              )}
              {visibleSeries.finalGrossProfit && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="finalGrossProfit"
                  stroke="#82ca9d"
                  name="Final Gross Profit $"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle>Margin Data</CardTitle>
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-sm relative">
              <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                <tr className="border-b dark:border-gray-700">
                  {columns.map((col, index) => (
                    <th
                      key={col.id}
                      className={`p-2 cursor-move select-none ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                        draggedColumn === index ? 'opacity-50' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data]
                  .sort((a, b) => {
                    const dateA = a.date?.toDate?.() ?? new Date(0);
                    const dateB = b.date?.toDate?.() ?? new Date(0);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .map((item, rowIndex) => (
                    <tr
                      key={item.id}
                      className={`border-b dark:border-gray-800 ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={`p-2 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                        >
                          {col.accessor(item)}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};